"use client";

// ─────────────────────────────────────────────────────────────────────────────
// Patharo Onboarding V2
// Conversational, mobile-first, glassmorphic, one question at a time.
// Each answer slides the next question in from the right.
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from "react";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";

// ─── Types ────────────────────────────────────────────────────────────────────

type GoalValue =
  | "keep_up" | "get_ahead" | "fix_gaps"
  | "test_prep" | "core_skills" | "topic_focus";

type Confidence = "confident" | "okay" | "struggling" | "very_difficult";
type LearningStyle = "visual" | "practice" | "step_by_step" | "interactive";
type SchedulePreference =
  | "light_daily" | "few_times_week" | "structured_daily" | "flexible";

interface SubjectPlan {
  subject:        string;
  goal:           GoalValue | null;
  selectedTopics: string[];
  confidence:     Confidence | null;
}

interface OnboardingData {
  childName:          string;
  grade:              string;
  frenchImmersion:    boolean | null; // null = don't know
  subjects:           string[];
  subjectPlans:       Record<string, SubjectPlan>;
  schedulePreference: SchedulePreference | null;
  learningStyles:     LearningStyle[];
  notifications:      { weeklyEmail: boolean; progressAlerts: boolean };
  rewards:            boolean;
}

// ─── Screen IDs ───────────────────────────────────────────────────────────────

type ScreenId =
  | "welcome"
  | "name"
  | "grade"
  | "french"
  | "subjects"
  | "subject_goal"   // repeats per subject
  | "subject_conf"   // repeats per subject
  | "schedule"
  | "style"
  | "settings"
  | "plan";

// ─── Goal options per subject ─────────────────────────────────────────────────

const GOALS: Record<string, { value: GoalValue; label: string; emoji: string }[]> = {
  _default: [
    { value: "keep_up",     label: "Keep up with school",       emoji: "📚" },
    { value: "get_ahead",   label: "Get ahead",                 emoji: "🚀" },
    { value: "fix_gaps",    label: "Fix gaps",                  emoji: "🔧" },
    { value: "test_prep",   label: "Test or exam prep",         emoji: "📝" },
    { value: "core_skills", label: "Build core skills",         emoji: "🧱" },
    { value: "topic_focus", label: "Focus on specific topics",  emoji: "🎯" },
  ],
  French: [
    { value: "keep_up",     label: "Keep up with FI class",     emoji: "📚" },
    { value: "get_ahead",   label: "Get ahead",                 emoji: "🚀" },
    { value: "fix_gaps",    label: "Fix gaps",                  emoji: "🔧" },
    { value: "test_prep",   label: "Test or exam prep",         emoji: "📝" },
    { value: "core_skills", label: "Build written grammar",     emoji: "✍️" },
    { value: "topic_focus", label: "Focus on specific topics",  emoji: "🎯" },
  ],
};

const getGoals = (subject: string) => GOALS[subject] ?? GOALS._default;

const CONFIDENCE = [
  { value: "confident"      as Confidence, label: "Confident",      emoji: "💪", color: "#1A9E78" },
  { value: "okay"           as Confidence, label: "Okay",           emoji: "👍", color: "#2264D1" },
  { value: "struggling"     as Confidence, label: "Struggling",     emoji: "😓", color: "#b45309" },
  { value: "very_difficult" as Confidence, label: "Very difficult",  emoji: "😰", color: "#dc2626" },
];

const SCHEDULES = [
  { value: "light_daily"      as SchedulePreference, label: "5–10 min a day",       emoji: "⚡" },
  { value: "few_times_week"   as SchedulePreference, label: "3–4 times per week",   emoji: "📅" },
  { value: "structured_daily" as SchedulePreference, label: "Daily sessions",        emoji: "🗓️" },
  { value: "flexible"         as SchedulePreference, label: "Flexible",              emoji: "🌊" },
];

const LEARNING_STYLES = [
  { value: "visual"       as LearningStyle, label: "Visual",         emoji: "👁️" },
  { value: "practice"     as LearningStyle, label: "Practice-based", emoji: "✏️" },
  { value: "step_by_step" as LearningStyle, label: "Step-by-step",   emoji: "🪜" },
  { value: "interactive"  as LearningStyle, label: "Interactive",    emoji: "🎮" },
];

const GRADES = ["1","2","3","4","5","6","7","8","9","10","11","12"];

const SUBJECT_EMOJI: Record<string, string> = {
  Math: "📐", English: "📖", Science: "🔬", French: "🇫🇷",
  History: "🏛️", Geography: "🌍", Physics: "⚡", Biology: "🧬",
  Chemistry: "🧪", "Computer Science": "💻", Functions: "📈",
  "Advanced Functions": "📊", "Calculus and Vectors": "∫",
  "Mathematics of Data Management": "📊", Spelling: "✍️", Civics: "⚖️",
};

const subEmoji = (s: string) => SUBJECT_EMOJI[s] ?? "📚";

const emptyPlan = (subject: string): SubjectPlan => ({
  subject, goal: null, selectedTopics: [], confidence: null,
});

// ─── Slide animation ──────────────────────────────────────────────────────────

const slideIn = {
  initial: { opacity: 0, x: 60  },
  animate: { opacity: 1, x: 0   },
  exit:    { opacity: 0, x: -60 },
  transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] as any },
};

// ─── Glassmorphic option button ───────────────────────────────────────────────

function GlassOption({
  emoji, label, selected, onClick, color, delay = 0, multiSelect = false,
}: {
  emoji?: string; label: string; selected: boolean; onClick: () => void;
  color?: string; delay?: number; multiSelect?: boolean;
}) {
  return (
    <motion.button
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0,  scale: 1     }}
      transition={{ delay, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      style={{
        width: "100%",
        padding: "18px 20px",
        borderRadius: 20,
        border: selected
          ? `2px solid ${color ?? "#2264D1"}`
          : "1.5px solid rgba(255,255,255,0.25)",
        background: selected
          ? `${color ?? "#2264D1"}18`
          : "rgba(255,255,255,0.12)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        boxShadow: selected
          ? `0 4px 24px ${color ?? "#2264D1"}30, inset 0 1px 0 rgba(255,255,255,0.2)`
          : "0 2px 12px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.15)",
        display: "flex",
        alignItems: "center",
        gap: 16,
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.2s ease",
        fontFamily: "'Outfit', sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Shimmer on selected */}
      {selected && (
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 60%)",
          pointerEvents: "none",
        }} />
      )}
      {emoji && (
        <span style={{ fontSize: 26, flexShrink: 0, lineHeight: 1 }}>{emoji}</span>
      )}
      <span style={{
        fontSize: 16, fontWeight: 600, color: "#fff",
        letterSpacing: "-0.2px", flex: 1,
      }}>
        {label}
      </span>
      {/* Check indicator */}
      <div style={{
        width: 22, height: 22, borderRadius: multiSelect ? 6 : "50%",
        border: selected
          ? `2px solid ${color ?? "#2264D1"}`
          : "2px solid rgba(255,255,255,0.3)",
        background: selected ? (color ?? "#2264D1") : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, transition: "all 0.2s",
      }}>
        {selected && (
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
            <path d="M1 4.5L4 7.5L10 1" stroke="white" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
    </motion.button>
  );
}

// ─── Polo guide bubble ────────────────────────────────────────────────────────

function GuideBubble({ message, name }: { message: string; name?: string }) {
  const personalised = name
    ? message.replace(/\[name\]/g, name)
    : message.replace(/\[name\]'s /g, "their ").replace(/\[name\]/g, "them");

  return (
    <motion.div
      key={message}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0   }}
      transition={{ duration: 0.4 }}
      style={{
        background: "rgba(255,255,255,0.15)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderRadius: 20,
        border: "1px solid rgba(255,255,255,0.25)",
        padding: "20px 22px",
        marginBottom: 28,
        boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
          background: "linear-gradient(135deg,#2563eb,#06b6d4)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, boxShadow: "0 2px 12px rgba(37,99,235,0.4)",
        }}>
          🐾
        </div>
        <p style={{
          fontSize: 16, color: "rgba(255,255,255,0.95)", lineHeight: 1.6,
          fontWeight: 400, margin: 0,
        }}>
          {personalised}
        </p>
      </div>
    </motion.div>
  );
}

// ─── Topic chip ───────────────────────────────────────────────────────────────

function TopicChip({
  label, selected, onClick,
}: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <motion.button
      whileTap={{ scale: 0.96 }}
      onClick={onClick}
      style={{
        padding: "10px 16px", borderRadius: 100, cursor: "pointer",
        border: selected
          ? "1.5px solid #60a5fa"
          : "1.5px solid rgba(255,255,255,0.2)",
        background: selected
          ? "rgba(96,165,250,0.2)"
          : "rgba(255,255,255,0.08)",
        fontSize: 13, fontWeight: 500,
        color: selected ? "#93c5fd" : "rgba(255,255,255,0.7)",
        fontFamily: "'Outfit', sans-serif",
        transition: "all 0.15s",
      }}
    >
      {label}
    </motion.button>
  );
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

function ProgressBar({ progress }: { progress: number }) {
  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, height: 3, zIndex: 100,
      background: "rgba(255,255,255,0.1)",
    }}>
      <motion.div
        animate={{ width: `${progress}%` }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        style={{
          height: "100%",
          background: "linear-gradient(90deg,#2563eb,#06b6d4)",
          borderRadius: "0 2px 2px 0",
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function OnboardingV2() {
  const router = useRouter();

  const [screen, setScreen]              = useState<ScreenId>("welcome");
  const [subjectIdx, setSubjectIdx]      = useState(0); // which subject we're on
  const [subjectPhase, setSubjectPhase]  = useState<"goal"|"conf">("goal");
  const [availableSubjects, setAvailableSubjects] = useState<string[]>([]);
  const [topicsMap, setTopicsMap]        = useState<Record<string, string[]>>({});
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [submitting, setSubmitting]      = useState(false);

  const [data, setData] = useState<OnboardingData>({
    childName:       "",
    grade:           "",
    frenchImmersion: null,
    subjects:        [],
    subjectPlans:    {},
    schedulePreference: null,
    learningStyles:  [],
    notifications:   { weeklyEmail: true, progressAlerts: true },
    rewards:         true,
  });

  // Fetch subjects when grade is set
  useEffect(() => {
    if (!data.grade) return;
    fetch(`/api/onboarding/subjects?grade=${data.grade}`)
      .then(r => r.json())
      .then(d => setAvailableSubjects(d.subjects ?? []))
      .catch(() => {
        const g = parseInt(data.grade);
        setAvailableSubjects(g <= 8
          ? ["Math","English","Science","French","History","Geography"]
          : ["Math","English","Physics","Chemistry","Biology","French",
             "Functions","Advanced Functions","History"]
        );
      });
  }, [data.grade]);

  // Fetch topics when topic_focus selected
  const fetchTopics = useCallback((subject: string) => {
    if (!data.grade || topicsMap[subject]) return;
    setTopicsLoading(true);
    fetch(`/api/onboarding/topics?subject=${encodeURIComponent(subject)}&grade=${data.grade}`)
      .then(r => r.json())
      .then(d => setTopicsMap(p => ({ ...p, [subject]: d.topics ?? [] })))
      .catch(() => setTopicsMap(p => ({ ...p, [subject]: [] })))
      .finally(() => setTopicsLoading(false));
  }, [data.grade, topicsMap]);

  // Current subject being configured
  const currentSubject = data.subjects[subjectIdx] ?? "";
  const currentPlan    = data.subjectPlans[currentSubject];

  // Progress calculation
  const SCREEN_ORDER: ScreenId[] = [
    "welcome","name","grade","french","subjects",
    "subject_goal","subject_conf",
    "schedule","style","settings","plan",
  ];

  const screenProgress: Record<ScreenId, number> = {
    welcome: 0, name: 5, grade: 12, french: 20, subjects: 28,
    subject_goal: 40, subject_conf: 55,
    schedule: 68, style: 78, settings: 88, plan: 100,
  };

  // Navigation helpers
  function advance(nextScreen: ScreenId) {
    setScreen(nextScreen);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goBack() {
    const map: Partial<Record<ScreenId, ScreenId>> = {
      name: "welcome", grade: "name", french: "grade",
      subjects: "french", subject_goal: "subjects",
      subject_conf: "subject_goal",
      schedule: "subjects", style: "schedule",
      settings: "style", plan: "settings",
    };
    if (screen === "subject_goal" && subjectIdx > 0) {
      setSubjectIdx(i => i - 1);
      setSubjectPhase("conf");
      setScreen("subject_conf");
      return;
    }
    if (screen === "subject_conf") {
      setSubjectPhase("goal");
      setScreen("subject_goal");
      return;
    }
    const prev = map[screen];
    if (prev) advance(prev);
  }

  // Subject helpers
  function toggleSubject(s: string) {
    setData(p => {
      const has      = p.subjects.includes(s);
      const subjects = has ? p.subjects.filter(x => x !== s) : [...p.subjects, s];
      const plans    = { ...p.subjectPlans };
      if (!has) plans[s] = emptyPlan(s);
      else delete plans[s];
      return { ...p, subjects, subjectPlans: plans };
    });
  }

  function updatePlan(subject: string, patch: Partial<SubjectPlan>) {
    setData(p => ({
      ...p,
      subjectPlans: { ...p.subjectPlans, [subject]: { ...p.subjectPlans[subject], ...patch } },
    }));
  }

  // After selecting goal — maybe fetch topics
  function handleGoalSelect(goal: GoalValue) {
    updatePlan(currentSubject, { goal });
    if (goal === "topic_focus") fetchTopics(currentSubject);
  }

  // After goal confirmed, go to confidence
  function advanceFromGoal() {
    setSubjectPhase("conf");
    advance("subject_conf");
  }

  // After confidence, either go to next subject or to schedule
  function handleConfidenceSelect(conf: Confidence) {
    updatePlan(currentSubject, { confidence: conf });
    if (subjectIdx < data.subjects.length - 1) {
      setSubjectIdx(i => i + 1);
      setSubjectPhase("goal");
      setTimeout(() => advance("subject_goal"), 100);
    } else {
      setTimeout(() => advance("schedule"), 100);
    }
  }

  // Toggle learning style
  function toggleStyle(s: LearningStyle) {
    setData(p => ({
      ...p,
      learningStyles: p.learningStyles.includes(s)
        ? p.learningStyles.filter(x => x !== s)
        : [...p.learningStyles, s],
    }));
  }

  // Finish
  function handleFinish() {
    setSubmitting(true);
    try {
      sessionStorage.setItem("patharo_onboarding", JSON.stringify({
        ...data, createdAt: new Date().toISOString(),
      }));
      router.push("/register");
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const name = data.childName.trim() || undefined;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Spectral:ital,wght@0,400;0,500;1,400&family=Outfit:wght@300;400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Outfit', sans-serif; -webkit-tap-highlight-color: transparent; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Full-screen gradient background */}
      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(160deg, #0d1b3e 0%, #0a2244 40%, #060e24 100%)",
        display: "flex", flexDirection: "column",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}>
        <ProgressBar progress={screenProgress[screen] ?? 0} />

        {/* Nav row */}
        {screen !== "welcome" && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "16px 20px 0",
          }}>
            <button onClick={goBack} style={{
              background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 12, padding: "8px 16px", color: "rgba(255,255,255,0.7)",
              fontSize: 14, fontWeight: 500, cursor: "pointer",
              fontFamily: "'Outfit',sans-serif", display: "flex", alignItems: "center", gap: 6,
            }}>
              ← Back
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8,
                background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 900, color: "#fff" }}>P</div>
              <span style={{ fontSize: 15, fontWeight: 600, color: "#fff",
                fontFamily: "'Spectral',serif" }}>Patharo</span>
            </div>
          </div>
        )}

        {/* Content area */}
        <div style={{
          flex: 1, display: "flex", flexDirection: "column",
          padding: screen === "welcome" ? 0 : "24px 20px 24px",
          maxWidth: 520, width: "100%", margin: "0 auto",
        }}>
          <AnimatePresence mode="wait">
            <motion.div key={screen + subjectIdx + subjectPhase} {...slideIn}
              style={{ flex: 1, display: "flex", flexDirection: "column" }}>

              {/* ══ WELCOME ══════════════════════════════════════════════════ */}
              {screen === "welcome" && (
                <div style={{
                  flex: 1, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  padding: "40px 24px", textAlign: "center",
                }}>
                  {/* Logo */}
                  <div style={{ marginBottom: 32 }}>
                    <div style={{
                      width: 72, height: 72, borderRadius: 22,
                      background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 32, margin: "0 auto 16px",
                      boxShadow: "0 8px 32px rgba(37,99,235,0.4)",
                    }}>🐾</div>
                    <h1 style={{
                      fontFamily: "'Spectral',serif", fontSize: 36, fontWeight: 500,
                      color: "#fff", letterSpacing: "-0.5px", lineHeight: 1.15,
                    }}>Welcome to<br />Patharo</h1>
                  </div>

                  {/* Message card */}
                  <div style={{
                    background: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
                    borderRadius: 24, border: "1px solid rgba(255,255,255,0.2)",
                    padding: "28px 24px", marginBottom: 32, textAlign: "left",
                    boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
                  }}>
                    <p style={{ fontSize: 16, color: "rgba(255,255,255,0.9)", lineHeight: 1.7, marginBottom: 16 }}>
                      We&apos;re going to build a <strong style={{ color: "#fff" }}>personalized learning plan</strong> for your child — it only takes about 2 minutes.
                    </p>
                    <p style={{ fontSize: 15, color: "rgba(255,255,255,0.7)", lineHeight: 1.7, marginBottom: 16 }}>
                      Patharo uses proven learning science — spaced repetition, targeted practice, and adaptive pacing — so your child doesn&apos;t just memorize, they actually <em>retain</em> what they learn.
                    </p>
                    <p style={{ fontSize: 15, color: "rgba(255,255,255,0.6)", lineHeight: 1.7 }}>
                      This plan is a starting point. You can always adjust it later. 🎯
                    </p>
                  </div>

                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={() => advance("name")}
                    style={{
                      width: "100%", maxWidth: 320, padding: "18px 24px",
                      borderRadius: 100, border: "none", cursor: "pointer",
                      background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                      color: "#fff", fontSize: 17, fontWeight: 700,
                      fontFamily: "'Outfit',sans-serif",
                      boxShadow: "0 8px 32px rgba(37,99,235,0.45)",
                      display: "flex", alignItems: "center",
                      justifyContent: "center", gap: 10,
                    }}
                  >
                    Let&apos;s get started →
                  </motion.button>

                  <p style={{ marginTop: 20, fontSize: 13, color: "rgba(255,255,255,0.35)" }}>
                    Ontario Grades 1–12 · English &amp; French Immersion
                  </p>
                </div>
              )}

              {/* ══ NAME ═════════════════════════════════════════════════════ */}
              {screen === "name" && (
                <>
                  <GuideBubble
                    message="Let's start with the basics! What's your child's name?"
                    name={name}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <input
                      autoFocus
                      type="text"
                      placeholder="e.g. Olivia"
                      value={data.childName}
                      onChange={e => setData(p => ({ ...p, childName: e.target.value }))}
                      onKeyDown={e => { if (e.key === "Enter" && data.childName.trim()) advance("grade"); }}
                      style={{
                        width: "100%", padding: "20px 22px", borderRadius: 20,
                        border: "1.5px solid rgba(255,255,255,0.25)",
                        background: "rgba(255,255,255,0.12)",
                        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
                        color: "#fff", fontSize: 20, fontWeight: 600,
                        fontFamily: "'Outfit',sans-serif", outline: "none",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.1)",
                      }}
                    />
                    <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", textAlign: "center" }}>
                      You can skip this if you prefer
                    </p>
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => advance("grade")}
                      style={{
                        width: "100%", padding: "18px", borderRadius: 100,
                        background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                        border: "none", color: "#fff", fontSize: 16, fontWeight: 700,
                        fontFamily: "'Outfit',sans-serif", cursor: "pointer",
                        boxShadow: "0 4px 24px rgba(37,99,235,0.4)", marginTop: 8,
                      }}
                    >
                      {data.childName.trim() ? `Continue with ${data.childName.trim()} →` : "Skip →"}
                    </motion.button>
                  </div>
                </>
              )}

              {/* ══ GRADE ════════════════════════════════════════════════════ */}
              {screen === "grade" && (
                <>
                  <GuideBubble
                    message={`Great${name ? `, ${name}` : ""}! What grade are they in?`}
                    name={name}
                  />
                  <div style={{
                    display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10,
                  }}>
                    {GRADES.map((g, i) => (
                      <motion.button
                        key={g}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.03 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          setData(p => ({ ...p, grade: g, subjects: [], subjectPlans: {} }));
                          setTimeout(() => advance("french"), 150);
                        }}
                        style={{
                          padding: "18px 8px", borderRadius: 18,
                          border: data.grade === g
                            ? "2px solid #60a5fa"
                            : "1.5px solid rgba(255,255,255,0.2)",
                          background: data.grade === g
                            ? "rgba(96,165,250,0.2)"
                            : "rgba(255,255,255,0.08)",
                          backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
                          cursor: "pointer", textAlign: "center",
                          fontFamily: "'Outfit',sans-serif",
                        }}
                      >
                        <div style={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>{g}</div>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>
                          {parseInt(g) <= 8 ? "Elem." : "H.S."}
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </>
              )}

              {/* ══ FRENCH ═══════════════════════════════════════════════════ */}
              {screen === "french" && (
                <>
                  <GuideBubble
                    message={`Is ${name ?? "your child"} in a French Immersion program?`}
                    name={name}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {[
                      { label: "Yes, French Immersion",  emoji: "🇫🇷", value: true  },
                      { label: "No, English program",     emoji: "🇬🇧", value: false },
                      { label: "Not sure",                emoji: "🤷", value: null  },
                    ].map((opt, i) => (
                      <GlassOption
                        key={String(opt.value)}
                        emoji={opt.emoji}
                        label={opt.label}
                        selected={data.frenchImmersion === opt.value}
                        delay={i * 0.06}
                        onClick={() => {
                          setData(p => ({ ...p, frenchImmersion: opt.value }));
                          setTimeout(() => advance("subjects"), 200);
                        }}
                      />
                    ))}
                  </div>
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", textAlign: "center", marginTop: 16 }}>
                    You can always update this in Settings
                  </p>
                </>
              )}

              {/* ══ SUBJECTS ══════════════════════════════════════════════════ */}
              {screen === "subjects" && (
                <>
                  <GuideBubble
                    message={`What subjects does ${name ?? "your child"} want to work on? Pick as many as you like.`}
                    name={name}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                    {availableSubjects.map((s, i) => (
                      <GlassOption
                        key={s}
                        emoji={subEmoji(s)}
                        label={s}
                        selected={data.subjects.includes(s)}
                        delay={i * 0.05}
                        multiSelect
                        onClick={() => toggleSubject(s)}
                      />
                    ))}
                  </div>
                  {data.subjects.length > 0 && (
                    <motion.button
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => { setSubjectIdx(0); setSubjectPhase("goal"); advance("subject_goal"); }}
                      style={{
                        width: "100%", padding: "18px", borderRadius: 100,
                        background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                        border: "none", color: "#fff", fontSize: 16, fontWeight: 700,
                        fontFamily: "'Outfit',sans-serif", cursor: "pointer",
                        boxShadow: "0 4px 24px rgba(37,99,235,0.4)",
                      }}
                    >
                      Continue with {data.subjects.length} subject{data.subjects.length > 1 ? "s" : ""} →
                    </motion.button>
                  )}
                </>
              )}

              {/* ══ SUBJECT GOAL ══════════════════════════════════════════════ */}
              {screen === "subject_goal" && currentSubject && (
                <>
                  <GuideBubble
                    message={`${subEmoji(currentSubject)} For ${currentSubject} — what's the main goal${name ? ` for ${name}` : ""}?`}
                    name={name}
                  />
                  {/* Subject progress dots */}
                  {data.subjects.length > 1 && (
                    <div style={{ display: "flex", gap: 6, marginBottom: 20, justifyContent: "center" }}>
                      {data.subjects.map((s, i) => (
                        <div key={s} style={{
                          height: 4, borderRadius: 2, transition: "all 0.3s",
                          width: i === subjectIdx ? 20 : 8,
                          background: i < subjectIdx
                            ? "#1A9E78"
                            : i === subjectIdx ? "#60a5fa" : "rgba(255,255,255,0.2)",
                        }} />
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {getGoals(currentSubject).map((g, i) => (
                      <div key={g.value}>
                        <GlassOption
                          emoji={g.emoji}
                          label={g.label}
                          selected={currentPlan?.goal === g.value}
                          delay={i * 0.05}
                          onClick={() => handleGoalSelect(g.value)}
                        />
                        {/* Topic chips expand inline */}
                        <AnimatePresence>
                          {currentPlan?.goal === "topic_focus" && g.value === "topic_focus" && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              style={{ overflow: "hidden" }}
                            >
                              <div style={{
                                margin: "8px 0 4px",
                                background: "rgba(96,165,250,0.08)",
                                border: "1px solid rgba(96,165,250,0.2)",
                                borderRadius: "0 0 16px 16px",
                                padding: "14px 16px",
                              }}>
                                {topicsLoading ? (
                                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>
                                    Loading topics…
                                  </p>
                                ) : (topicsMap[currentSubject]?.length ?? 0) === 0 ? (
                                  <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>
                                    Polo will choose the best starting topics for you.
                                  </p>
                                ) : (
                                  <>
                                    <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: 10 }}>
                                      Select topics to focus on (optional)
                                    </p>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                      {(topicsMap[currentSubject] ?? []).map(t => (
                                        <TopicChip
                                          key={t} label={t}
                                          selected={currentPlan?.selectedTopics?.includes(t) ?? false}
                                          onClick={() => {
                                            const curr = currentPlan?.selectedTopics ?? [];
                                            updatePlan(currentSubject, {
                                              selectedTopics: curr.includes(t)
                                                ? curr.filter(x => x !== t)
                                                : [...curr, t],
                                            });
                                          }}
                                        />
                                      ))}
                                    </div>
                                  </>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                  {currentPlan?.goal && (
                    <motion.button
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={advanceFromGoal}
                      style={{
                        width: "100%", padding: "18px", borderRadius: 100, marginTop: 16,
                        background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                        border: "none", color: "#fff", fontSize: 16, fontWeight: 700,
                        fontFamily: "'Outfit',sans-serif", cursor: "pointer",
                        boxShadow: "0 4px 24px rgba(37,99,235,0.4)",
                      }}
                    >
                      Continue →
                    </motion.button>
                  )}
                </>
              )}

              {/* ══ SUBJECT CONFIDENCE ════════════════════════════════════════ */}
              {screen === "subject_conf" && currentSubject && (
                <>
                  <GuideBubble
                    message={`How confident is ${name ?? "your child"} in ${currentSubject}?`}
                    name={name}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {CONFIDENCE.map((c, i) => (
                      <GlassOption
                        key={c.value}
                        emoji={c.emoji}
                        label={c.label}
                        selected={currentPlan?.confidence === c.value}
                        color={c.color}
                        delay={i * 0.06}
                        onClick={() => handleConfidenceSelect(c.value)}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* ══ SCHEDULE ══════════════════════════════════════════════════ */}
              {screen === "schedule" && (
                <>
                  <GuideBubble
                    message={`Almost there! How often would ${name ?? "your child"} like to practice?`}
                    name={name}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {SCHEDULES.map((s, i) => (
                      <GlassOption
                        key={s.value}
                        emoji={s.emoji}
                        label={s.label}
                        selected={data.schedulePreference === s.value}
                        delay={i * 0.06}
                        onClick={() => {
                          setData(p => ({ ...p, schedulePreference: s.value }));
                          setTimeout(() => advance("style"), 200);
                        }}
                      />
                    ))}
                  </div>
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", textAlign: "center", marginTop: 14 }}>
                    Consistency matters more than duration — even 5 minutes a day works.
                  </p>
                </>
              )}

              {/* ══ LEARNING STYLE ════════════════════════════════════════════ */}
              {screen === "style" && (
                <>
                  <GuideBubble
                    message={`How does ${name ?? "your child"} learn best? Pick all that apply.`}
                    name={name}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                    {LEARNING_STYLES.map((s, i) => (
                      <GlassOption
                        key={s.value}
                        emoji={s.emoji}
                        label={s.label}
                        selected={data.learningStyles.includes(s.value)}
                        delay={i * 0.06}
                        multiSelect
                        onClick={() => toggleStyle(s.value)}
                      />
                    ))}
                  </div>
                  {data.learningStyles.length > 0 && (
                    <motion.button
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => advance("settings")}
                      style={{
                        width: "100%", padding: "18px", borderRadius: 100,
                        background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                        border: "none", color: "#fff", fontSize: 16, fontWeight: 700,
                        fontFamily: "'Outfit',sans-serif", cursor: "pointer",
                        boxShadow: "0 4px 24px rgba(37,99,235,0.4)",
                      }}
                    >
                      Continue →
                    </motion.button>
                  )}
                </>
              )}

              {/* ══ SETTINGS ══════════════════════════════════════════════════ */}
              {screen === "settings" && (
                <>
                  <GuideBubble
                    message="Last thing — a few preferences. You can change these any time."
                    name={name}
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                    {[
                      {
                        emoji: "📧", label: "Weekly progress email",
                        value: data.notifications.weeklyEmail,
                        onChange: () => setData(p => ({ ...p, notifications: { ...p.notifications, weeklyEmail: !p.notifications.weeklyEmail } })),
                      },
                      {
                        emoji: "🔔", label: "Progress alerts",
                        value: data.notifications.progressAlerts,
                        onChange: () => setData(p => ({ ...p, notifications: { ...p.notifications, progressAlerts: !p.notifications.progressAlerts } })),
                      },
                      {
                        emoji: "🏆", label: "Polo rewards & XP",
                        value: data.rewards,
                        onChange: () => setData(p => ({ ...p, rewards: !p.rewards })),
                      },
                    ].map((item, i) => (
                      <motion.div
                        key={item.label}
                        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.07 }}
                        onClick={item.onChange}
                        style={{
                          padding: "18px 20px", borderRadius: 20, cursor: "pointer",
                          border: item.value
                            ? "1.5px solid rgba(96,165,250,0.5)"
                            : "1.5px solid rgba(255,255,255,0.15)",
                          background: item.value
                            ? "rgba(96,165,250,0.1)"
                            : "rgba(255,255,255,0.06)",
                          backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
                          display: "flex", alignItems: "center", gap: 14,
                          userSelect: "none",
                        }}
                      >
                        <span style={{ fontSize: 24 }}>{item.emoji}</span>
                        <span style={{ fontSize: 16, fontWeight: 600, color: "#fff", flex: 1 }}>
                          {item.label}
                        </span>
                        {/* Toggle */}
                        <div style={{
                          width: 46, height: 26, borderRadius: 100, flexShrink: 0,
                          background: item.value
                            ? "linear-gradient(135deg,#2563eb,#06b6d4)"
                            : "rgba(255,255,255,0.15)",
                          position: "relative", transition: "background 0.2s",
                        }}>
                          <div style={{
                            position: "absolute", top: 3,
                            left: item.value ? "calc(100% - 22px)" : 3,
                            width: 20, height: 20, borderRadius: "50%",
                            background: "#fff",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                            transition: "left 0.2s",
                          }} />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <motion.button
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => advance("plan")}
                    style={{
                      width: "100%", padding: "18px", borderRadius: 100,
                      background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                      border: "none", color: "#fff", fontSize: 16, fontWeight: 700,
                      fontFamily: "'Outfit',sans-serif", cursor: "pointer",
                      boxShadow: "0 4px 24px rgba(37,99,235,0.4)",
                    }}
                  >
                    See the plan →
                  </motion.button>
                </>
              )}

              {/* ══ PLAN ══════════════════════════════════════════════════════ */}
              {screen === "plan" && (
                <>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    style={{ textAlign: "center", marginBottom: 28 }}
                  >
                    <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
                    <h2 style={{
                      fontFamily: "'Spectral',serif", fontSize: 28, fontWeight: 500,
                      color: "#fff", letterSpacing: "-0.4px", marginBottom: 8,
                    }}>
                      {name ? `${name}'s plan is ready!` : "The plan is ready!"}
                    </h2>
                    <p style={{ fontSize: 15, color: "rgba(255,255,255,0.6)", lineHeight: 1.6 }}>
                      Grade {data.grade}{data.frenchImmersion ? " · French Immersion" : ""}
                    </p>
                  </motion.div>

                  {/* Subject cards */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                    {data.subjects.map((s, i) => {
                      const sp     = data.subjectPlans[s];
                      const goal   = getGoals(s).find(g => g.value === sp?.goal);
                      const conf   = CONFIDENCE.find(c => c.value === sp?.confidence);
                      return (
                        <motion.div
                          key={s}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.08 }}
                          style={{
                            padding: "18px 20px", borderRadius: 20,
                            background: "rgba(255,255,255,0.1)",
                            backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.15)",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                            <span style={{ fontSize: 22 }}>{subEmoji(s)}</span>
                            <span style={{ fontSize: 17, fontWeight: 700, color: "#fff" }}>{s}</span>
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {goal && (
                              <span style={{
                                fontSize: 12, fontWeight: 600, padding: "4px 12px",
                                borderRadius: 100, background: "rgba(96,165,250,0.15)",
                                border: "1px solid rgba(96,165,250,0.3)", color: "#93c5fd",
                              }}>
                                {goal.emoji} {goal.label}
                              </span>
                            )}
                            {conf && (
                              <span style={{
                                fontSize: 12, fontWeight: 600, padding: "4px 12px",
                                borderRadius: 100, background: `${conf.color}18`,
                                border: `1px solid ${conf.color}40`, color: conf.color,
                              }}>
                                {conf.emoji} {conf.label}
                              </span>
                            )}
                            {(sp?.selectedTopics?.length ?? 0) > 0 && (
                              <span style={{
                                fontSize: 12, fontWeight: 500, padding: "4px 12px",
                                borderRadius: 100, background: "rgba(255,255,255,0.08)",
                                border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)",
                              }}>
                                🎯 {sp!.selectedTopics.length} topic{sp!.selectedTopics.length > 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Schedule chip */}
                  {data.schedulePreference && (
                    <div style={{
                      padding: "14px 18px", borderRadius: 16, marginBottom: 24,
                      background: "rgba(255,255,255,0.07)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      display: "flex", alignItems: "center", gap: 10,
                    }}>
                      <span style={{ fontSize: 20 }}>
                        {SCHEDULES.find(s => s.value === data.schedulePreference)?.emoji}
                      </span>
                      <div>
                        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginBottom: 2 }}>Schedule</p>
                        <p style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>
                          {SCHEDULES.find(s => s.value === data.schedulePreference)?.label}
                        </p>
                      </div>
                    </div>
                  )}

                  <motion.button
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={handleFinish}
                    disabled={submitting}
                    style={{
                      width: "100%", padding: "20px", borderRadius: 100,
                      background: "linear-gradient(135deg,#2563eb,#06b6d4)",
                      border: "none", color: "#fff", fontSize: 17, fontWeight: 700,
                      fontFamily: "'Outfit',sans-serif", cursor: "pointer",
                      boxShadow: "0 8px 32px rgba(37,99,235,0.5)",
                      opacity: submitting ? 0.6 : 1,
                    }}
                  >
                    {submitting ? "Saving…" : "Create my account & save plan →"}
                  </motion.button>

                  <p style={{
                    fontSize: 12, color: "rgba(255,255,255,0.3)",
                    textAlign: "center", marginTop: 14, lineHeight: 1.6,
                  }}>
                    You&apos;ll create a free account on the next screen to save this plan.
                  </p>
                </>
              )}

            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}